import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Clock, X } from 'lucide-react'
import { buttonVariants } from '@/components/ui'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { planExpiryMessage, planExpiryNotice, type PlanExpiryNoticeData } from './planExpiry'

/**
 * R10-08 — faixa no topo do app do aluno nos ultimos dias do acesso, com o botao para o
 * checkout do plano pago configurado no plano. Fechar vale para a sessao do navegador
 * (o aviso volta no proximo acesso, ate o plano acabar).
 */
export function PlanExpiryNotice() {
  const access = useAuthStore((state) => state.user?.plan_access)
  const notice = planExpiryNotice(access, new Date())
  const key = notice ? dismissKey(notice) : null
  const [dismissed, setDismissed] = useState(() => (key ? readDismissed(key) : false))

  if (!notice || !key || dismissed) return null

  const close = () => {
    writeDismissed(key)
    setDismissed(true)
  }

  return (
    <div
      role="status"
      className="mb-4 flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 sm:flex-row sm:items-center dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
    >
      <Clock className="hidden h-5 w-5 flex-shrink-0 sm:block" aria-hidden />
      <p className="flex-1">
        <strong className="font-semibold">{planExpiryMessage(notice)}</strong>
        {notice.upgradePath ? ' Assine para continuar usando a plataforma.' : ' Fale com a equipe MedHunter para continuar.'}
      </p>
      <div className="flex items-center gap-2">
        {notice.upgradePath && (
          <Link to={notice.upgradePath} className={cn(buttonVariants({ variant: 'primary', size: 'sm' }))}>
            Assinar {notice.upgradePlanName ?? 'agora'}
          </Link>
        )}
        <button
          type="button"
          onClick={close}
          aria-label="Fechar aviso"
          className="rounded-lg p-1.5 hover:bg-amber-100 dark:hover:bg-amber-500/20"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  )
}

// A chave leva a data de fim: renovou ou trocou de plano, o aviso novo nao fica escondido.
function dismissKey(notice: PlanExpiryNoticeData): string {
  return `medhunter-plan-expiry-dismissed:${notice.planId}:${notice.expiresAt}`
}

// sessionStorage pode faltar ou lancar (aba anonima, storage bloqueado): sem ele, o
// aviso so deixa de ser lembrado entre telas, nunca quebra a pagina.
function readDismissed(key: string): boolean {
  try {
    return window.sessionStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

function writeDismissed(key: string): void {
  try {
    window.sessionStorage.setItem(key, '1')
  } catch {
    // Sem storage: o aviso fecha so nesta tela.
  }
}
